// Canonical Broiler Batches route contracts.
//
// /broiler/batches/* is one router splat with two independently released
// surfaces. The raw predicates here deliberately admit less than the router:
// the bare hub and one decoded-safe batch-name segment. Every other historic or
// malformed shape is refused from the released renderer and handled by App's
// explicit route-recovery disposition.

import {isCanonicalCommentAnchorHash} from './commentAnchorHash.ts';

export const BROILER_BATCHES_INDEX_PATHNAME = '/broiler/batches';
export const BROILER_BATCHES_DETAIL_PATHNAME_PREFIX = '/broiler/batches/';

type LocationLike = Readonly<{pathname?: unknown; search?: unknown; hash?: unknown}>;

function isSafeRawSegment(value: string): boolean {
  return !(
    value === '' ||
    value.includes('/') ||
    value.includes('\\') ||
    value.includes('\0') ||
    value.includes('\r') ||
    value.includes('\n') ||
    value.includes('?') ||
    value.includes('#')
  );
}

function isSafeDecodedSegment(value: string): boolean {
  return (
    value.trim() !== '' &&
    value !== '.' &&
    value !== '..' &&
    !value.includes('/') &&
    !value.includes('\\') &&
    !value.includes('\0') &&
    !value.includes('\r') &&
    !value.includes('\n') &&
    !value.includes('?') &&
    !value.includes('#')
  );
}

export function isCanonicalBroilerBatchesIndexPathname(pathname: unknown): pathname is string {
  return pathname === BROILER_BATCHES_INDEX_PATHNAME;
}

// The hub reads no location state; search/hash are presentation-only here.
// A bare string is still just a pathname. Object callers must prove that both
// presentation fields, when present, are strings rather than arbitrary state.
export function isCanonicalBroilerBatchesIndexLocation(location: unknown): boolean {
  if (typeof location === 'string') return isCanonicalBroilerBatchesIndexPathname(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;
  const {pathname, search, hash} = location as LocationLike;
  return (
    isCanonicalBroilerBatchesIndexPathname(pathname) &&
    (search === undefined || typeof search === 'string') &&
    (hash === undefined || typeof hash === 'string')
  );
}

export function isCanonicalBroilerBatchesDetailPathname(pathname: unknown): pathname is string {
  if (typeof pathname !== 'string' || !pathname.startsWith(BROILER_BATCHES_DETAIL_PATHNAME_PREFIX)) return false;
  const rawName = pathname.slice(BROILER_BATCHES_DETAIL_PATHNAME_PREFIX.length);
  if (!isSafeRawSegment(rawName)) return false;
  try {
    return isSafeDecodedSegment(decodeURIComponent(rawName));
  } catch {
    return false;
  }
}

// The record page consumes location.hash for deep-link scrolling. Query and
// fragment variants therefore remain outside the released claim until their
// behaviour is reviewed.
export function isCanonicalBroilerBatchesDetailLocation(location: unknown): boolean {
  if (typeof location === 'string') return isCanonicalBroilerBatchesDetailPathname(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;
  const {pathname, search, hash} = location as LocationLike;
  return (
    (search === undefined || search === '') &&
    (hash === undefined || hash === '' || isCanonicalCommentAnchorHash(hash)) &&
    isCanonicalBroilerBatchesDetailPathname(pathname)
  );
}
